import {
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react"
import { CoverPageOfferFragment as CoverPageOffer } from "@pathwright/graphql-client/src/generated"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useRef, useState } from "react"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import type { RegistrationAccessRow } from "../registration/access"
import {
  getCoverPageAnotherActionLabels,
  getPrivateCohortAnotherAction
} from "./coverPageAnotherAction"
import type { CoverPageContinueState } from "./coverPageContinue"
import CoverPageContinueButton from "./CoverPageContinueButton"
import { getOfferContinueOptions } from "./coverPageOfferOrdering"
import { useCoverPageContext } from "./CoverPageProvider"
import { useCoverPageContinue } from "./useCoverPageContinue"
import { useCoverPageTranslate } from "./utils"
import {
  CoverPageInvitation,
  inviteRequiresAcceptance
} from "./coverPageInviteAccess"

const gateContinueButtonProps = {
  variant: "solid" as const,
  colorPalette: "brand",
  textDecoration: "none",
  fontWeight: "700",
  size: "xl" as const,
  w: "100%",
  maxW: "320px",
  _hover: { textDecoration: "none", opacity: 0.9 }
}

type CoverPageOfferExistingAccessPanelProps = {
  accesses: RegistrationAccessRow[]
  canCreateAnother: boolean
  anotherEllipsisLabel?: string
  anotherQuestionLabel?: string
  onCreateAnother: () => void
}

/**
 * Post–sign-in panel when the user already has RegistrationAccess for this Offer.
 * Reuses CoverPageContinueButton (single Resume / multi cohort picker).
 */
export function CoverPageOfferExistingAccessPanel({
  accesses,
  canCreateAnother,
  anotherEllipsisLabel,
  anotherQuestionLabel,
  onCreateAnother
}: CoverPageOfferExistingAccessPanelProps) {
  const { t } = useCoverPageTranslate()
  const multiple = accesses.length > 1
  const primary = accesses[0]

  return (
    <VStack
      className="CoverPageOfferExistingAccessGate"
      gap={4}
      w="full"
      align="center"
      textAlign="center"
      py={6}
      px={4}
    >
      <Heading as="h2" size="lg" m={0}>
        {t("offer.access.already_have_access", {
          defaultValue: "You already have access."
        })}
      </Heading>

      {!multiple && primary && (
        <HStack gap={2} justify="center">
          <Pathicon icon="group" />
          <Text fontWeight="600" m={0}>
            {primary.cohort_name}
          </Text>
        </HStack>
      )}

      <CoverPageContinueButton
        accesses={accesses}
        canCreateAnother={canCreateAnother && multiple}
        anotherActionLabel={anotherEllipsisLabel}
        onCreateAnother={onCreateAnother}
        buttonProps={gateContinueButtonProps}
      />

      {!multiple && canCreateAnother && anotherQuestionLabel && (
        <Button
          variant="plain"
          size="sm"
          h="auto"
          minW="unset"
          p={0}
          fontSize="sm"
          fontWeight="normal"
          textDecoration="underline"
          color="inherit"
          onClick={onCreateAnother}
        >
          {anotherQuestionLabel}
        </Button>
      )}
    </VStack>
  )
}

export function CoverPageOfferAccessDetectionPending() {
  return (
    <VStack
      className="CoverPageOfferAccessDetectionPending"
      w="full"
      py={10}
      align="center"
      justify="center"
    >
      <Spinner size="lg" color="blackAlpha.600" borderWidth="3px" />
    </VStack>
  )
}

// Note: very edge case that a user would reach this panel. Our queries are
// refetched by default up to 5 times before throwing, and a network connection
// would likely fail before that.
export function CoverPageOfferAccessDetectionErrorPanel({
  onRetry,
  retrying = false
}: {
  onRetry: () => void
  retrying?: boolean
}) {
  const { t } = useCoverPageTranslate()

  return (
    <VStack
      className="CoverPageOfferAccessDetectionError"
      gap={4}
      w="full"
      align="center"
      textAlign="center"
      py={6}
      px={4}
    >
      <Heading as="h2" size="lg" m={0}>
        {t("offer.access.detection_failed_title", {
          defaultValue: "Couldn't check your access"
        })}
      </Heading>

      <Text m={0} maxW="360px">
        {t("offer.access.detection_failed_body", {
          defaultValue:
            "We couldn't determine whether you already have access to this Path. Retry before joining or creating another."
        })}
      </Text>

      <Button {...gateContinueButtonProps} loading={retrying} onClick={onRetry}>
        {t("offer.access.detection_retry", { defaultValue: "Retry" })}
      </Button>
    </VStack>
  )
}

function ExistingAccessFromContinueState({
  offer,
  state,
  onCreateAnother
}: {
  offer: CoverPageOffer
  state: CoverPageContinueState
  onCreateAnother: () => void
}) {
  const { t } = useCoverPageTranslate()
  const anotherAction = getPrivateCohortAnotherAction({
    offer,
    accesses: state.accesses
  })
  const labels = getCoverPageAnotherActionLabels(anotherAction.kind)

  return (
    <CoverPageOfferExistingAccessPanel
      accesses={state.accesses}
      canCreateAnother={anotherAction.show}
      anotherEllipsisLabel={
        labels
          ? t(labels.ellipsisKey, { defaultValue: labels.ellipsisDefault })
          : undefined
      }
      anotherQuestionLabel={
        labels
          ? t(labels.questionKey, { defaultValue: labels.questionDefault })
          : undefined
      }
      onCreateAnother={onCreateAnother}
    />
  )
}

export type OfferExistingAccessGateRenderProps = {
  /**
   * True only once existing-access detection has succeeded with no matching
   * access (or the gate is bypassed). Callers must gate `attemptToConvert` on
   * this so conversion cannot run while lookup is pending or failed.
   */
  conversionAllowed: boolean
}

export type OfferExistingAccessGateChildren =
  | React.ReactNode
  | ((props: OfferExistingAccessGateRenderProps) => React.ReactNode)

function renderGateChildren(
  children: OfferExistingAccessGateChildren,
  conversionAllowed: boolean
) {
  return typeof children === "function"
    ? children({ conversionAllowed })
    : children
}

/**
 * Keep RegisterFlow under one parent. Moving it between wrappers remounts it,
 * which drops the selected plan and can replay an automatic conversion.
 */
function StableConversion({
  hidden,
  children
}: {
  hidden: boolean
  children: React.ReactNode
}) {
  return (
    <Box
      display={hidden ? "none" : "contents"}
      visibility={hidden ? "hidden" : undefined}
      pointerEvents={hidden ? "none" : undefined}
      aria-hidden={hidden || undefined}
    >
      {children}
    </Box>
  )
}

/**
 * bypass — thank-you, a signed-in cover-card click, or "Create another?"
 * signed-out — conversion UI owns auth, but must not auto-convert yet
 * pending / failed — lookup has not produced a trustworthy answer
 * existing-access — first successful lookup found a matching cohort
 * clear — first successful lookup found none
 */
type AccessDetectionPhase =
  | "bypass"
  | "signed-out"
  | "pending"
  | "failed"
  | "existing-access"
  | "clear"

function accessDetectionPhase({
  bypass,
  signedIn,
  ready,
  failed,
  hadExistingAccess
}: {
  bypass: boolean
  signedIn: boolean
  ready: boolean
  failed: boolean
  hadExistingAccess: boolean | null
}): AccessDetectionPhase {
  if (bypass) return "bypass"
  if (!signedIn) return "signed-out"
  // Once conversion is admitted, its own loading/error/confirmation UI owns
  // the visit. Background access refreshes must not hide or restart it.
  if (hadExistingAccess === false) return "clear"
  if (failed) return "failed"
  // Null means the first successful lookup has not been recorded yet.
  if (!ready || hadExistingAccess === null) return "pending"
  return hadExistingAccess ? "existing-access" : "clear"
}

/**
 * Intercept Offer conversion in the Augmentation Panel when the user already
 * has matching RegistrationAccess.
 *
 * The gate runs after an in-panel sign-in, and for a signed-in visitor who
 * opened the panel directly. It does not run for a signed-in cover-card click
 * (`offerConversionIntentId`), "Create another?", or a thank-you/Stripe return.
 *
 * Conversion stays mounted throughout so auth and checkout state survive.
 * `conversionAllowed` becomes true only for `bypass` and `clear`.
 */
export function OfferExistingAccessGate({
  offer,
  /**
   * Thank-you / Stripe return. The remounted panel is signed in and now has
   * access, but must keep showing RegisterFlow confirmation.
   */
  suppress = false,
  invitation,
  children
}: {
  offer: CoverPageOffer
  suppress?: boolean
  invitation?: CoverPageInvitation | null
  children: OfferExistingAccessGateChildren
}) {
  const { me } = usePathwrightContext()
  // Frozen at mount: a later in-panel sign-in is not an intentional convert.
  const authenticatedAtMount = useRef(!!me).current
  const offerConversionIntentId = useCoverPageContext(
    (v) => v.offerConversionIntentId
  )
  const resourceId = useCoverPageContext((v) => v.resourceId)
  const [dismissed, setDismissed] = useState(false)

  const continueState = useCoverPageContinue(
    resourceId,
    getOfferContinueOptions(offer)
  )

  // Freeze the entry decision before conversion. Access created by accepting
  // this invitation belongs to the current flow, not the already-access gate.
  // In particular, do not skip recording an invite's empty/lower-role lookup:
  // the next refresh may already include its new registration.
  const hadExistingAccess = useRef<boolean | null>(null)
  const conversionBypassesDetection =
    suppress ||
    dismissed ||
    (authenticatedAtMount && offerConversionIntentId === offer.id)
  if (
    !conversionBypassesDetection &&
    me &&
    continueState.ready &&
    !continueState.loading &&
    hadExistingAccess.current === null
  ) {
    hadExistingAccess.current = invitation
      ? !inviteRequiresAcceptance(invitation, continueState.accesses)
      : continueState.mode !== "none"
  }
  const invitationNeedsAcceptance =
    !!invitation && hadExistingAccess.current === false
  const bypass = conversionBypassesDetection || invitationNeedsAcceptance

  const phase = accessDetectionPhase({
    bypass,
    signedIn: !!me,
    ready: continueState.ready,
    failed: !!continueState.error,
    hadExistingAccess: hadExistingAccess.current
  })
  // Signed-out is the exception: its conversion UI stays visible for auth,
  // while attemptToConvert waits for a successful empty lookup.
  const showConversion =
    phase === "bypass" || phase === "signed-out" || phase === "clear"
  const conversionAllowed = phase === "bypass" || phase === "clear"

  return (
    <>
      {phase === "failed" && (
        <CoverPageOfferAccessDetectionErrorPanel
          onRetry={() => {
            void continueState.refetch()
          }}
          retrying={continueState.loading}
        />
      )}
      {phase === "pending" && <CoverPageOfferAccessDetectionPending />}
      {phase === "existing-access" && (
        <ExistingAccessFromContinueState
          offer={offer}
          state={continueState}
          onCreateAnother={() => setDismissed(true)}
        />
      )}
      <StableConversion key="conversion" hidden={!showConversion}>
        {renderGateChildren(children, conversionAllowed)}
      </StableConversion>
    </>
  )
}

export default OfferExistingAccessGate
